import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import PasswordResetPage from './PasswordResetPage';

const BANNER_URL = 'https://iis.memphis.edu/wp-content/uploads/2025/03/cropped-UofM_horiz_rgb.jpg';
const HEALTH_API = '/health';

const TOKEN_KEY = 'jwt_token';

const AdminPortalPage = () => {
  const apiPrefix = window.location.pathname.match(/^\/[^\/]+/) ? window.location.pathname.match(/^\/[^\/]+/)[0].replace(/\/$/, '') : '';
  // Maintenance mode quick toggle
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceError, setMaintenanceError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    if (!isAuthenticated) {
      console.warn('isAuthenticated is false or undefined:', isAuthenticated);
      return;
    }
    setMaintenanceLoading(true);
    setMaintenanceError('');
    authFetch('/api/maintenance')
      .then(res => res.json())
      .then(data => {
        setMaintenanceEnabled(!!data.enabled);
        setMaintenanceMessage(data.message || '');
      })
      .catch(() => setMaintenanceError('Failed to fetch maintenance status.'))
      .finally(() => setMaintenanceLoading(false));
  }, [isAuthenticated]);

  const handleMaintenanceToggle = async () => {
    setMaintenanceLoading(true);
    setMaintenanceError('');
    try {
      const res = await authFetch('/api/maintenance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !maintenanceEnabled, message: maintenanceMessage })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMaintenanceEnabled(data.enabled);
      } else {
        setMaintenanceError(data.error || 'Failed to update maintenance mode.');
      }
    } catch {
      setMaintenanceError('Failed to update maintenance mode.');
    } finally {
      setMaintenanceLoading(false);
    }
  };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [assignedInstance, setAssignedInstance] = useState(null);
  const [instanceLoading, setInstanceLoading] = useState(false);
  const [instanceError, setInstanceError] = useState('');
  const [accessRequests, setAccessRequests] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [instanceCounts, setInstanceCounts] = useState({});

  // Changelog management state
  const [currentChangelog, setCurrentChangelog] = useState(null);
  const [changelogVersion, setChangelogVersion] = useState('');
  const [changelogChanges, setChangelogChanges] = useState(['']);
  const [changelogNotes, setChangelogNotes] = useState('');
  const [changelogLoading, setChangelogLoading] = useState(false);
  const [changelogError, setChangelogError] = useState('');
  const [changelogSuccess, setChangelogSuccess] = useState('');
  const [allChangelogs, setAllChangelogs] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Temporary password modal state
  const [showTempPasswordModal, setShowTempPasswordModal] = useState(false);
  const [tempPasswordData, setTempPasswordData] = useState(null);
  const [tempPasswordError, setTempPasswordError] = useState('');
  const [tempPasswordCopied, setTempPasswordCopied] = useState(false);

  // Password reset page state
  const [requiresPasswordReset, setRequiresPasswordReset] = useState(false);
  const [passwordResetUserId, setPasswordResetUserId] = useState(null);
  const [passwordResetUserEmail, setPasswordResetUserEmail] = useState(null);

  // Helper to get token
  const getToken = () => localStorage.getItem(TOKEN_KEY);

  // Helper to add Authorization header
  const authFetch = React.useCallback((url, options = {}) => {
    const token = getToken();
    return fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
  }, []);

  // Memoize fetchUsers and useCallback for stable dependencies
  const fetchUsers = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch('/api/users');
      const data = await response.json();
      if (response.ok) {
        setUsers(data);
      } else {
        setError(data.error || 'Failed to fetch users.');
      }
    } catch (err) {
      setError('Error fetching users.');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    const fetchHealth = async () => {
      setHealthLoading(true);
      try {
        const res = await fetch(apiPrefix + HEALTH_API);
        const data = await res.json();
        setHealth(data);
        if (data && data.activeUsersPerInstance) setInstanceCounts(data.activeUsersPerInstance);
      } catch {
        setHealth(null);
      } finally {
        setHealthLoading(false);
      }
    };
    fetchHealth();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchUsers();
    const fetchInstance = async () => {
      setInstanceLoading(true);
      setInstanceError('');
      try {
        const res = await authFetch('/api/my-instance');
        const data = await res.json();
        if (res.ok && data.instance) {
          setAssignedInstance(data.instance);
        } else {
          setAssignedInstance(null);
          setInstanceError(data.message || 'No instance assigned.');
        }
      } catch (err) {
        setAssignedInstance(null);
        setInstanceError('Error fetching instance assignment.');
      } finally {
        setInstanceLoading(false);
      }
    };
    fetchInstance();
    const fetchAccessRequests = async () => {
      setAccessLoading(true);
      setAccessError('');
      try {
        const res = await authFetch('/api/access-requests');
        const data = await res.json();
        if (res.ok) {
          setAccessRequests(data);
        } else {
          setAccessError(data.error || 'Failed to fetch access requests.');
        }
      } catch (err) {
        setAccessError('Error fetching access requests.');
      } finally {
        setAccessLoading(false);
      }
    };
    fetchAccessRequests();
  }, [isAuthenticated, fetchUsers, authFetch]);

  // Changelog management functions
  const fetchCurrentChangelog = React.useCallback(async () => {
    try {
      const res = await authFetch('/api/changelog');
      const data = await res.json();
      if (res.ok) {
        setCurrentChangelog(data);
        setChangelogVersion(data.version || '');
        setChangelogChanges(data.changes || ['']);
        setChangelogNotes(data.notes || '');
      }
    } catch (err) {
      console.error('Error fetching current changelog:', err);
    }
  }, [authFetch]);

  const fetchAllChangelogs = React.useCallback(async () => {
    try {
      const res = await authFetch('/api/changelogs');
      const data = await res.json();
      if (res.ok) {
        setAllChangelogs(data);
      }
    } catch (err) {
      console.error('Error fetching all changelogs:', err);
    }
  }, [authFetch]);

  // Fetch current changelog when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchCurrentChangelog();
      fetchAllChangelogs();
    }
  }, [isAuthenticated, fetchCurrentChangelog, fetchAllChangelogs]);

  const handleDeleteChangelog = async (version) => {
    if (!window.confirm(`Are you sure you want to delete changelog version ${version}? This action cannot be undone.`)) {
      return;
    }

    setDeleteLoading(true);
    setChangelogError('');
    setChangelogSuccess('');

    try {
      const res = await authFetch(`/api/changelog/${version}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        setChangelogSuccess(`Changelog version ${version} deleted successfully!`);
        await fetchAllChangelogs();
        await fetchCurrentChangelog();
      } else {
        const data = await res.json();
        setChangelogError(data.error || 'Failed to delete changelog');
      }
    } catch (err) {
      setChangelogError('Error deleting changelog');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSaveChangelog = async (e) => {
    e.preventDefault();
    setChangelogLoading(true);
    setChangelogError('');
    setChangelogSuccess('');

    // Filter out empty changes
    const filteredChanges = changelogChanges.filter(change => change.trim() !== '');

    if (!changelogVersion.trim()) {
      setChangelogError('Version is required');
      setChangelogLoading(false);
      return;
    }

    if (filteredChanges.length === 0) {
      setChangelogError('At least one change is required');
      setChangelogLoading(false);
      return;
    }

    try {
      const res = await authFetch('/api/changelog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          version: changelogVersion.trim(),
          changes: filteredChanges,
          notes: changelogNotes.trim() || undefined
        })
      });

      const data = await res.json();

      if (res.ok) {
        setChangelogSuccess(data.message || 'Changelog saved successfully!');
        await fetchCurrentChangelog(); // Refresh current changelog
      } else {
        setChangelogError(data.error || 'Failed to save changelog');
      }
    } catch (err) {
      setChangelogError('Error saving changelog');
    } finally {
      setChangelogLoading(false);
    }
  };

  const handleAddChange = () => {
    setChangelogChanges([...changelogChanges, '']);
  };

  const handleRemoveChange = (index) => {
    if (changelogChanges.length > 1) {
      const newChanges = changelogChanges.filter((_, i) => i !== index);
      setChangelogChanges(newChanges);
    }
  };

  const handleChangeUpdate = (index, value) => {
    const newChanges = [...changelogChanges];
    newChanges[index] = value;
    setChangelogChanges(newChanges);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const response = await fetch(apiPrefix + '/api/admin-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      const data = await response.json();
      if (response.ok && data.requiresPasswordReset) {
        // User has a temporary password and needs to reset it
        setPasswordResetUserId(data.userId);
        setPasswordResetUserEmail(data.email);
        setRequiresPasswordReset(true);
      } else if (response.ok && data.isAdmin && data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
        setIsAuthenticated(true);
      } else {
        setLoginError(data.error || 'Invalid credentials or not an admin.');
      }
    } catch (err) {
      setLoginError('Login failed.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setIsAuthenticated(false);
    setUsers([]);
    setAccessRequests([]);
    setAssignedInstance(null);
  };

  const handlePasswordResetComplete = (token) => {
    // After successful password reset, store the token and mark as authenticated
    localStorage.setItem(TOKEN_KEY, token);
    setIsAuthenticated(true);
    setRequiresPasswordReset(false);
    setPasswordResetUserId(null);
    setPasswordResetUserEmail(null);
  };

  // Helper: AccessRequestRow component and approve handler
  function AccessRequestRow({ req, onApprove, refreshUsers }) {
    const [approving, setApproving] = useState(false);
    const [denying, setDenying] = useState(false);
    const [error, setError] = useState('');
    // Debug: log the request object
    useEffect(() => {
      if (!req || !(req._id || req.id)) {
        console.error('AccessRequestRow: Missing or invalid req.id/_id', req);
      }
    }, [req]);
    const handleApprove = async () => {
      const requestId = req._id || req.id;
      if (!req || !requestId) {
        setError('Invalid request: missing request ID.');
        return;
      }
      setApproving(true);
      setError('');
      try {
        const res = await authFetch(`/api/access-requests/${requestId}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });
        const data = await res.json();
        if (res.ok) {
          onApprove();
          if (refreshUsers) refreshUsers();
        } else {
          setError(data.error || 'Failed to approve request.');
        }
      } catch (err) {
        setError('Error approving request.');
      } finally {
        setApproving(false);
      }
    };

    const handleDeny = async () => {
      const requestId = req._id || req.id;
      if (!req || !requestId) {
        setError('Invalid request: missing request ID.');
        return;
      }
      if (!window.confirm('Are you sure you want to deny this request? The user will lose provisional access.')) {
        return;
      }
      setDenying(true);
      setError('');
      try {
        const res = await authFetch(`/api/access-requests/${requestId}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        });
        const data = await res.json();
        if (res.ok) {
          onApprove();
          if (refreshUsers) refreshUsers();
        } else {
          setError(data.error || 'Failed to deny request.');
        }
      } catch (err) {
        setError('Error denying request.');
      } finally {
        setDenying(false);
      }
    };

    return (
      <tr>
        <td>{req.email}</td>
        <td>{req.ip}</td>
        <td>{new Date(req.createdAt).toLocaleString()}</td>
        <td>
          {req.provisional && (
            <span className="badge bg-info me-2">Provisional Access</span>
          )}
        </td>
        <td>
          <button 
            className="btn btn-success btn-sm me-2" 
            onClick={handleApprove} 
            disabled={approving || denying}
            title="Approve this request - user keeps access"
          >
            {approving ? 'Approving...' : 'Approve'}
          </button>
          <button 
            className="btn btn-danger btn-sm" 
            onClick={handleDeny} 
            disabled={denying || approving}
            title="Deny this request - user loses access"
          >
            {denying ? 'Denying...' : 'Deny'}
          </button>
          {error && <div className="text-danger small mt-1">{error}</div>}
        </td>
      </tr>
    );
  }

  // Remove request from list after approval
  function handleApproveRequest(requestId) {
    // After approval, refresh both users and access requests from backend
    fetchUsers();
    // Also refresh access requests to remove the approved one and reflect any new requests
    const fetchAccessRequests = async () => {
      setAccessLoading(true);
      setAccessError('');
      try {
        const res = await authFetch('/api/access-requests');
        const data = await res.json();
        if (res.ok) {
          setAccessRequests(data);
        } else {
          setAccessError(data.error || 'Failed to fetch access requests.');
        }
      } catch (err) {
        setAccessError('Error fetching access requests.');
      } finally {
        setAccessLoading(false);
      }
    };
    fetchAccessRequests();
  }

  // Add AutoAssignButton component
  function AutoAssignButton({ userId, lastKnownIp, onAssigned }) {
    const [assigning, setAssigning] = useState(false);
    const [error, setError] = useState('');
    if (!lastKnownIp) return null; // Only show if we have an IP to assign
    const handleAssign = async () => {
      setAssigning(true);
      setError('');
      try {
        // Use authFetch to include JWT
        const res = await authFetch(`/api/users/${userId}/auto-assign`, { method: 'POST' });
        const data = await res.json();
        if (res.ok) {
          onAssigned && onAssigned();
        } else {
          setError(data.error || 'Failed to auto-assign.');
        }
      } catch (err) {
        setError('Error auto-assigning.');
      } finally {
        setAssigning(false);
      }
    };
    return (
      <>
        <button className="btn btn-primary btn-sm ms-2" onClick={handleAssign} disabled={assigning}>
          {assigning ? 'Assigning...' : 'Auto-Assign'}
        </button>
        {error && <div className="text-danger small mt-1">{error}</div>}
      </>
    );
  }

  // Add AssignToCurrentIpButton component
  function AssignToCurrentIpButton({ userId, onAssigned }) {
    const [assigning, setAssigning] = useState(false);
    const [error, setError] = useState('');
    const handleAssign = async () => {
      setAssigning(true);
      setError('');
      try {
        // Use authFetch to include JWT
        const res = await authFetch(`/api/users/${userId}/assign-to-current-ip`, { method: 'POST' });
        const data = await res.json();
        if (res.ok) {
          onAssigned && onAssigned();
        } else {
          setError(data.error || 'Failed to assign to current IP.');
        }
      } catch (err) {
        setError('Error assigning to current IP.');
      } finally {
        setAssigning(false);
      }
    };
    return (
      <>
        <button className="btn btn-secondary btn-sm ms-2" onClick={handleAssign} disabled={assigning}>
          {assigning ? 'Assigning...' : 'Assign to Current IP'}
        </button>
        {error && <div className="text-danger small mt-1">{error}</div>}
      </>
    );
  }

  // Add Block/Delete IP and Mapping buttons to user table
  function BlockDeleteButtons({ user, onAction }) {
    const [blockLoading, setBlockLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const ip = user.lastKnownIp;
    if (!ip) return (
      <div className="d-flex flex-column align-items-start">
        <DeleteUserButton userId={user._id} onDeleted={onAction} />
      </div>
    );
    // TODO: Review/remove these endpoints if ip_instance_map is deprecated
    const handleBlock = async () => {
      setBlockLoading(true); setError('');
      try {
        // Use authFetch to include JWT
        const res = await authFetch('/api/block-ip', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ip })
        });
        const data = await res.json();
        if (res.ok) onAction && onAction();
        else setError(data.error || 'Failed to block IP.');
      } catch (e) { setError('Error blocking IP.'); }
      setBlockLoading(false);
    };
    const handleDelete = async () => {
      setDeleteLoading(true); setError('');
      try {
        // Use authFetch to include JWT
        const res = await authFetch(`/api/ip-instance-map/${ip}`, { method: 'DELETE' });
        const data = await res.json();
        if (res.ok) onAction && onAction();
        else setError(data.error || 'Failed to delete mapping.');
      } catch (e) { setError('Error deleting mapping.'); }
      setDeleteLoading(false);
    };
    return (
      <div className="d-flex flex-column align-items-start">
        <button className="btn btn-danger btn-sm mb-1" onClick={handleBlock} disabled={blockLoading}>Block IP</button>
        <button className="btn btn-warning btn-sm mb-1" onClick={handleDelete} disabled={deleteLoading}>Delete Mapping</button>
        <DeleteUserButton userId={user._id} onDeleted={onAction} />
        {error && <div className="text-danger small mt-1">{error}</div>}
      </div>
    );
  }

  // Add DeleteUserButton component
  function DeleteUserButton({ userId, onDeleted }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const handleDeleteUser = async () => {
      if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
      setLoading(true);
      setError('');
      try {
        const res = await authFetch(`/api/users/${userId}`, { method: 'DELETE' });
        const data = await res.json();
        if (res.ok) {
          onDeleted && onDeleted();
        } else {
          setError(data.error || 'Failed to delete user.');
        }
      } catch (e) {
        setError('Error deleting user.');
      } finally {
        setLoading(false);
      }
    };
    return (
      <button className="btn btn-outline-danger btn-sm" onClick={handleDeleteUser} disabled={loading} style={{ marginTop: '4px' }}>
        {loading ? 'Deleting...' : 'Delete User'}
      </button>
    );
  }

  // Add RemoveInstanceButton component
  function RemoveInstanceButton({ userId, onRemoved }) {
    const [removing, setRemoving] = useState(false);
    const [error, setError] = useState('');
    const handleRemove = async () => {
      if (!window.confirm('Remove this user\'s Overleaf instance assignment?')) return;
      setRemoving(true);
      setError('');
      try {
        const res = await authFetch(`/api/users/${userId}/unassign-instance`, { method: 'POST' });
        const data = await res.json();
        if (res.ok) {
          onRemoved && onRemoved();
        } else {
          setError(data.error || 'Failed to remove instance.');
        }
      } catch (err) {
        setError('Error removing instance.');
      } finally {
        setRemoving(false);
      }
    };
    return (
      <>
        <button className="btn btn-outline-warning btn-sm ms-2" onClick={handleRemove} disabled={removing}>
          {removing ? 'Removing...' : 'Remove Instance'}
        </button>
        {error && <div className="text-danger small mt-1">{error}</div>}
      </>
    );
  }

  // Add MakeAdminButton component
  function MakeAdminButton({ userId, onMadeAdmin }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const handleMakeAdmin = async () => {
      if (!window.confirm('Are you sure you want to make this user an admin?')) return;
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        const res = await fetch(`${apiPrefix}/api/users/${userId}/make-admin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        });
        const data = await res.json();
        if (res.ok) {
          onMadeAdmin && onMadeAdmin();
        } else {
          setError(data.error || 'Failed to make user admin.');
        }
      } catch (e) {
        setError('Error making user admin.');
      } finally {
        setLoading(false);
      }
    };
    return (
      <button className="btn btn-outline-success btn-sm ms-2" onClick={handleMakeAdmin} disabled={loading}>
        {loading ? 'Making Admin...' : 'Make Admin'}
      </button>
    );
  }

  // Add GenerateTempPasswordButton component
  function GenerateTempPasswordButton({ userId, userEmail, onGenerated }) {
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');
    const handleGeneratePassword = async () => {
      if (!window.confirm(`Generate a temporary password for ${userEmail}? They will be required to set a new password on first login.`)) return;
      setGenerating(true);
      setError('');
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        const res = await fetch(`${apiPrefix}/api/admin/generate-temp-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ userId })
        });
        const data = await res.json();
        if (res.ok) {
          setTempPasswordData(data);
          setShowTempPasswordModal(true);
          onGenerated && onGenerated();
        } else {
          setError(data.error || 'Failed to generate temporary password');
        }
      } catch (err) {
        setError('Error generating temporary password');
      } finally {
        setGenerating(false);
      }
    };
    return (
      <>
        <button className="btn btn-outline-info btn-sm ms-2" onClick={handleGeneratePassword} disabled={generating}>
          {generating ? 'Generating...' : 'Reset Password'}
        </button>
        {error && <div className="text-danger small mt-1">{error}</div>}
      </>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <header className="bg-white shadow-sm">
        <div className="container-fluid p-0">
          <img src={BANNER_URL} alt="Institute for Intelligent Systems Banner" className="img-fluid w-100" style={{ maxHeight: '90px', objectFit: 'contain', background: '#fff' }} />
        </div>
      </header>
      <div className="container py-4">
        <div className="mb-4">
          <div className="card border-info shadow-sm">
            <div className="card-body d-flex align-items-center gap-3 flex-wrap">
              <span className="badge bg-info text-dark fs-6 px-3 py-2">Server Health</span>
              {healthLoading ? (
                <span className="text-secondary">Checking....</span>
              ) : health ? (
                <>
                  <span className="fw-bold text-success">{health.status === 'healthy' ? 'Healthy' : 'Unhealthy'}</span>
                  <span className="text-muted small ms-3">Uptime: {Math.floor(health.uptime)}s</span>
                  <span className="text-muted small ms-3">Instances: {health.overleafInstances}</span>
                  <span className="text-muted small ms-3">Current Target: {health.currentTarget}</span>
                  {instanceCounts && Object.keys(instanceCounts).length > 0 && (
                    <span className="text-muted small ms-3">
                      <b>Active Users/Instance:</b> {Object.entries(instanceCounts).map(([inst, cnt]) => `${inst}: ${cnt}`).join(', ')}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-danger">Unable to fetch health status</span>
              )}
            </div>
          </div>
        </div>
        {!isAuthenticated ? (
          requiresPasswordReset ? (
            <PasswordResetPage 
              userId={passwordResetUserId} 
              email={passwordResetUserEmail}
              onPasswordReset={handlePasswordResetComplete}
            />
          ) : (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
            <form onSubmit={handleLogin} className="card shadow p-4" style={{ maxWidth: '400px', width: '100%' }}>
              <h3 className="mb-3 text-center">Admin Login</h3>
              <div className="mb-3">
                <label htmlFor="adminEmail" className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  id="adminEmail"
                  value={loginEmail}
                  onChange={e => setLoginEmail(e.target.value)}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="adminPassword" className="form-label">Password</label>
                <input
                  type="password"
                  className="form-control"
                  id="adminPassword"
                  value={loginPassword}
                  onChange={e => setLoginPassword(e.target.value)}
                  required
                />
              </div>
              {loginError && <div className="alert alert-danger text-center">{loginError}</div>}
              <button type="submit" className="btn btn-primary w-100" disabled={loginLoading}>
                {loginLoading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          </div>
          )
        ) : (
          <div className="card shadow-lg p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2 className="card-title text-center mb-0">Admin Portal Page</h2>
              <div className="d-flex align-items-center gap-2">
                <button className="btn btn-outline-danger" onClick={handleLogout}>Logout</button>
                <button className={`btn btn-${maintenanceEnabled ? 'warning' : 'outline-warning'} ms-2`} onClick={handleMaintenanceToggle} disabled={maintenanceLoading}>
                  {maintenanceLoading ? 'Saving...' : maintenanceEnabled ? 'Disable Maintenance' : 'Enable Maintenance'}
                </button>
                <a href="../maintenance" className="btn btn-outline-secondary ms-2">Edit Message</a>
              </div>
              {maintenanceError && <div className="text-danger small mt-2">{maintenanceError}</div>}
            </div>
            {loading && <p>Loading users...</p>}
            {error && <p className="text-danger">{error}</p>}
            {!loading && !error && (
              <div className="table-responsive">
                <table className="table table-bordered align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Email</th>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>Admin</th>
                      <th>Assigned Instance</th>
                      <th>Approved IPs</th>
                      <th>Pending IPs</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, idx) => (
                      <tr key={user._id || user.email || idx}>
                        <td>{user.email}</td>
                        <td>{user.first_name}</td>
                        <td>{user.last_name}</td>
                        <td>{user.isAdmin ? 'Yes' : <>
                          <span className="text-danger">No</span>
                          {/* Render MakeAdminButton for non-admin users */}
                          <MakeAdminButton userId={user._id} onMadeAdmin={fetchUsers} />
                        </>}</td>
                        <td>{user.assignedInstance ? <>
                          <span className="text-primary">{user.assignedInstance}</span>
                          <RemoveInstanceButton userId={user._id} onRemoved={fetchUsers} />
                        </> : <>
                          <span className="text-muted">Unassigned</span>
                          <AutoAssignButton userId={user._id} lastKnownIp={user.lastKnownIp} onAssigned={fetchUsers} />
                        </>}</td>
                        <td>{Array.isArray(user.approvedIps) && user.approvedIps.length > 0 ? (
                          <ul className="mb-0 ps-3">
                            {user.approvedIps.map((ipObj, i) => {
                              if (typeof ipObj === 'string') return <li key={ipObj}>{ipObj}</li>;
                              if (ipObj && typeof ipObj === 'object') {
                                return <li key={ipObj.ip || i}>{ipObj.ip}{ipObj.addedAt ? <span className="text-muted small ms-2">({new Date(ipObj.addedAt).toLocaleString()})</span> : null}</li>;
                              }
                              return null;
                            })}
                          </ul>
                        ) : <span className="text-muted">None</span>}</td>
                        <td>{Array.isArray(user.pendingIps) && user.pendingIps.length > 0 ? (
                          <ul className="mb-0 ps-3">
                            {user.pendingIps.map((ipObj, i) => {
                              if (typeof ipObj === 'string') return <li key={ipObj}>{ipObj}</li>;
                              if (ipObj && typeof ipObj === 'object') {
                                return <li key={ipObj.ip || i}>{ipObj.ip}{ipObj.addedAt ? <span className="text-muted small ms-2">({new Date(ipObj.addedAt).toLocaleString()})</span> : null}</li>;
                              }
                              return null;
                            })}
                          </ul>
                        ) : <span className="text-muted">None</span>}</td>
                        <td><BlockDeleteButtons user={user} onAction={fetchUsers} />
                          <AssignToCurrentIpButton userId={user._id} onAssigned={fetchUsers} />
                          <GenerateTempPasswordButton userId={user._id} userEmail={user.email} onGenerated={fetchUsers} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Show error below the table if present */}
                {error && <div className="alert alert-danger mt-3">{error}</div>}
              </div>
            )}
            {isAuthenticated && (
              <div className="mb-3">
                <div className="alert alert-secondary d-flex align-items-center gap-2 mb-0" role="alert">
                  <span className="fw-bold">Your assigned Overleaf instance:</span>
                  {instanceLoading ? (
                    <span className="text-muted">Checking...</span>
                  ) : assignedInstance ? (
                    <span className="text-primary">{assignedInstance}</span>
                  ) : (
                    <span className="text-danger">{instanceError || 'Not assigned'}</span>
                  )}
                </div>
              </div>
            )}
            {isAuthenticated && (
              <div className="mb-4">
                <div className="card border-warning shadow-sm">
                  <div className="card-header bg-warning text-dark fw-bold">Pending Access Requests</div>
                  <div className="card-body">
                    {accessLoading ? (
                      <span>Loading...</span>
                    ) : accessError ? (
                      <span className="text-danger">{accessError}</span>
                    ) : accessRequests.length === 0 ? (
                      <span className="text-success">No pending requests.</span>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-bordered align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Email</th>
                              <th>IP</th>
                              <th>Requested At</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {accessRequests.map((req, idx) => (
                              <AccessRequestRow key={req._id || req.id || req.ip || idx} req={req} onApprove={() => handleApproveRequest(req._id || req.id)} refreshUsers={fetchUsers} />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Changelog Management Section */}
            <div className="card border-primary shadow-sm mt-4">
              <div className="card-header bg-primary text-white">
                <h5 className="card-title mb-0">
                  <i className="bi bi-journal-text me-2"></i>
                  Changelog Management
                </h5>
              </div>
              <div className="card-body">
                {changelogSuccess && (
                  <div className="alert alert-success alert-dismissible fade show" role="alert">
                    {changelogSuccess}
                    <button type="button" className="btn-close" onClick={() => setChangelogSuccess('')}></button>
                  </div>
                )}
                {changelogError && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {changelogError}
                    <button type="button" className="btn-close" onClick={() => setChangelogError('')}></button>
                  </div>
                )}

                <form onSubmit={handleSaveChangelog}>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="changelogVersion" className="form-label">Version *</label>
                      <input
                        type="text"
                        className="form-control"
                        id="changelogVersion"
                        value={changelogVersion}
                        onChange={(e) => setChangelogVersion(e.target.value)}
                        placeholder="e.g., 1.2.0"
                        required
                      />
                    </div>
                    <div className="col-md-6 d-flex align-items-end">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="loadCurrent"
                          onChange={(e) => {
                            if (e.target.checked && currentChangelog) {
                              setChangelogVersion(currentChangelog.version || '');
                              setChangelogChanges(currentChangelog.changes || ['']);
                              setChangelogNotes(currentChangelog.notes || '');
                            }
                          }}
                        />
                        <label className="form-check-label" htmlFor="loadCurrent">
                          Load Current Changelog
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Changes *</label>
                    {changelogChanges.map((change, index) => (
                      <div key={index} className="input-group mb-2">
                        <input
                          type="text"
                          className="form-control"
                          value={change}
                          onChange={(e) => handleChangeUpdate(index, e.target.value)}
                          placeholder={`Change ${index + 1}`}
                          required={index === 0}
                        />
                        {changelogChanges.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => handleRemoveChange(index)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleAddChange}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Add Change
                    </button>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="changelogNotes" className="form-label">Additional Notes (Optional)</label>
                    <textarea
                      className="form-control"
                      id="changelogNotes"
                      rows={3}
                      value={changelogNotes}
                      onChange={(e) => setChangelogNotes(e.target.value)}
                      placeholder="Any additional notes or information..."
                    />
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={changelogLoading}
                    >
                      {changelogLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-save me-1"></i>
                          Save Changelog
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setChangelogVersion('');
                        setChangelogChanges(['']);
                        setChangelogNotes('');
                        setChangelogError('');
                        setChangelogSuccess('');
                      }}
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Clear Form
                    </button>
                  </div>
                </form>

                {currentChangelog && (
                  <div className="mt-4">
                    <h6 className="text-muted">Current Published Changelog:</h6>
                    <div className="border rounded p-3 bg-light">
                      <div className="mb-2">
                        <strong>Version:</strong> {currentChangelog.version}
                      </div>
                      <div className="mb-2">
                        <strong>Changes:</strong>
                        <ul className="mb-0 mt-1">
                          {currentChangelog.changes?.map((change, index) => (
                            <li key={index}>{change}</li>
                          ))}
                        </ul>
                      </div>
                      {currentChangelog.notes && (
                        <div>
                          <strong>Notes:</strong> {currentChangelog.notes}
                        </div>
                      )}
                      <div className="text-muted small mt-2">
                        Last updated: {new Date(currentChangelog.date).toLocaleString()}
                      </div>
                    </div>
                  </div>
                )}

                {/* All Changelogs Section */}
                <div className="mt-4">
                  <h6 className="text-muted mb-3">All Changelog Entries:</h6>
                  {allChangelogs.length === 0 ? (
                    <p className="text-muted">No changelog entries found.</p>
                  ) : (
                    <div className="row">
                      {allChangelogs.map((changelog, index) => (
                        <div key={changelog.version || index} className="col-md-6 mb-3">
                          <div className="card h-100">
                            <div className="card-header d-flex justify-content-between align-items-center">
                              <h6 className="card-title mb-0">Version {changelog.version}</h6>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleDeleteChangelog(changelog.version)}
                                disabled={deleteLoading}
                              >
                                {deleteLoading ? (
                                  <span className="spinner-border spinner-border-sm" role="status"></span>
                                ) : (
                                  <i className="bi bi-trash"></i>
                                )}
                              </button>
                            </div>
                            <div className="card-body">
                              <div className="mb-2">
                                <strong>Changes:</strong>
                                <ul className="mb-0 mt-1 small">
                                  {changelog.changes?.slice(0, 3).map((change, idx) => (
                                    <li key={idx}>{change.length > 50 ? `${change.substring(0, 50)}...` : change}</li>
                                  ))}
                                  {changelog.changes?.length > 3 && (
                                    <li className="text-muted">...and {changelog.changes.length - 3} more</li>
                                  )}
                                </ul>
                              </div>
                              {changelog.notes && (
                                <div className="mb-2">
                                  <strong>Notes:</strong>
                                  <p className="small mb-0 text-muted">{changelog.notes.length > 100 ? `${changelog.notes.substring(0, 100)}...` : changelog.notes}</p>
                                </div>
                              )}
                              <div className="text-muted small">
                                Created: {new Date(changelog.date).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Temporary Password Modal */}
        {showTempPasswordModal && tempPasswordData && (
          <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Temporary Password Generated</h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setShowTempPasswordModal(false);
                      setTempPasswordCopied(false);
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  {tempPasswordError && (
                    <div className="alert alert-danger" role="alert">
                      {tempPasswordError}
                    </div>
                  )}
                  <div className="mb-3">
                    <p className="text-muted mb-3">
                      A temporary password has been generated for <strong>{tempPasswordData.email}</strong>.
                    </p>
                    <div className="alert alert-warning mb-3">
                      <strong>Important:</strong> Copy this password and manually email it to the user. The password will expire in 24 hours.
                    </div>
                    <label className="form-label">Temporary Password:</label>
                    <div className="input-group mb-3">
                      <input 
                        type="text" 
                        className="form-control font-monospace" 
                        value={tempPasswordData.tempPassword}
                        readOnly 
                      />
                      <button 
                        className="btn btn-outline-secondary" 
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(tempPasswordData.tempPassword);
                          setTempPasswordCopied(true);
                          setTimeout(() => setTempPasswordCopied(false), 2000);
                        }}
                      >
                        {tempPasswordCopied ? '✓ Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                  <div className="alert alert-info mb-0">
                    <strong>Instructions for user:</strong>
                    <ul className="mb-0 mt-2">
                      <li>Use this temporary password to log in</li>
                      <li>You will be prompted to set a new password immediately</li>
                      <li>This temporary password expires in 24 hours</li>
                      <li>If the password expires, ask the admin to generate a new one</li>
                    </ul>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowTempPasswordModal(false);
                      setTempPasswordCopied(false);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <footer className="bg-white border-top shadow-sm mt-auto py-3">
        <div className="container text-center">
          <a href="/portal-admin" className="btn btn-outline-primary me-2">Admin Portal</a>
          <a href="../maintenance" className="btn btn-outline-warning me-2">Maintenance Mode</a>
          <a href="../install-packages" className="btn btn-outline-success me-2">Install Packages</a>
          <a href="../import" className="btn btn-outline-info">Import Project</a>
        </div>
      </footer>
    </div>
  );
};

export default AdminPortalPage;
